<template>
  <div class="page-container">
    <PageHeader title="用户统计" show-breadcrumb />

    <!-- 搜索面板：按用户ID/部门/用户名/时间段筛选；不选日期=开赛累计，选了日期=区间现算 -->
    <SearchPanel>
      <el-input v-model.trim="query.zauId" placeholder="用户ID" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-select v-model="query.zadId" placeholder="部门" clearable size="small" filterable style="width:160px">
        <el-option v-for="d in deptOptions" :key="d.zadId" :label="d.zadName" :value="d.zadId" />
      </el-select>
      <el-input v-model.trim="query.zauName" placeholder="用户名" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-date-picker
        v-model="query.dateRange"
        type="daterange"
        size="small"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        :picker-options="pickerOptions"
        style="width:260px"
      />
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
        <!-- 导出：按部门/用户名条件导出用户统计 Excel -->
        <el-button type="success" icon="el-icon-download" size="small" :loading="exportLoading" @click="handleExport">导出</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="PK用户统计列表">
      <el-table :data="list" v-loading="loading" size="small" stripe :height="tableHeight">
        <el-table-column type="index" label="序号" width="80" align="center" />
        <el-table-column label="用户" min-width="140" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.zauName || ('用户' + row.zauId) }}</template>
        </el-table-column>
        <el-table-column prop="zadName" label="部门" min-width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.zadName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="pusPkNums" label="PK场次" min-width="100" align="center" />
        <el-table-column prop="pusPkWinNums" label="胜场" min-width="90" align="center" />
        <el-table-column label="总积分" min-width="100" align="center">
          <template slot-scope="{ row }"><span style="color:#d69e2e;font-weight:700">{{ row.pusPointsTotal }}</span></template>
        </el-table-column>
        <el-table-column label="胜率" min-width="170" align="center">
          <template slot-scope="{ row }">
            <el-progress :percentage="Number(row.pusPkWinRate) || 0" :stroke-width="10" :format="p => p + '%'" style="max-width:150px;margin:0 auto" />
          </template>
        </el-table-column>
        <el-table-column label="最高连胜" min-width="100" align="center">
          <template slot-scope="{ row }"><el-tag size="small" type="danger">{{ row.pusWinningStreakMax }}</el-tag></template>
        </el-table-column>
        <el-table-column label="当前连胜" min-width="100" align="center">
          <template slot-scope="{ row }"><el-tag size="small" type="warning">{{ row.pusWinningStreakNow }}</el-tag></template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#409eff" @click="handleDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 用户统计详情 -->
    <el-dialog title="用户统计详情" :visible.sync="detailVisible" width="520px" v-dialog-drag-resize>
      <div v-loading="detailLoading" class="stat-detail">
        <template v-if="detail">
          <div class="stat-row"><span>用户</span><b>{{ detail.zauName || ('用户' + detail.zauId) }}</b></div>
          <div class="stat-row"><span>部门</span><b>{{ detail.zadName || '-' }}</b></div>
          <div class="stat-row"><span>PK场次</span><b>{{ detail.pusPkNums }}</b></div>
          <div class="stat-row"><span>胜场</span><b>{{ detail.pusPkWinNums }}</b></div>
          <div class="stat-row"><span>总积分</span><b>{{ detail.pusPointsTotal }}</b></div>
          <div class="stat-row"><span>胜率</span><b>{{ detail.pusPkWinRate }}%</b></div>
          <div class="stat-row"><span>最高连胜</span><b>{{ detail.pusWinningStreakMax }}</b></div>
          <div class="stat-row"><span>当前连胜</span><b>{{ detail.pusWinningStreakNow }}</b></div>
        </template>
      </div>
      <div slot="footer"><el-button size="small" @click="detailVisible = false">关闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getPkqUserStatisticsList, getPkqUserStatisticsByZauId, exportPkqUserStatisticsList, getZqpkAuthDepartmentList } from '@/api/api'

export default {
  name: 'PkqUserStatistics',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      exportLoading: false,
      tableHeight: 400,
      list: [],
      deptOptions: [],
      query: { zauId: '', zadId: null, zauName: '', dateRange: null },
      // 结束日期不能选未来（统计只到已有数据）
      pickerOptions: { disabledDate (d) { return d.getTime() > Date.now() } },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      detailVisible: false,
      detailLoading: false,
      detail: null
    }
  },
  created () { this.fetchList(); this.fetchDepartments() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    calcTableHeight () {
      const el = this.$el.querySelector('.el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 80
    },
    async fetchList () {
      this.loading = true
      try {
        // 只传有值的筛选条件，避免空串/null 传给后端数字参数
        const params = { currentPage: this.page.currentPage, pageSize: this.page.pageSize }
        if (this.query.zauId) params.zauId = this.query.zauId
        if (this.query.zadId !== null && this.query.zadId !== undefined) params.zadId = this.query.zadId
        if (this.query.zauName) params.zauName = this.query.zauName
        // 时间段(yyyy-MM-dd)：daterange 必然成对；后端按区间现算，不传为累计口径
        if (this.query.dateRange && this.query.dateRange.length === 2) {
          params.startDate = this.query.dateRange[0]
          params.endDate = this.query.dateRange[1]
        }
        const res = await getPkqUserStatisticsList(params)
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { zauId: '', zadId: null, zauName: '', dateRange: null }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    // 部门下拉数据（部门量不大，一次取全量）
    async fetchDepartments () {
      try {
        const res = await getZqpkAuthDepartmentList({ currentPage: 1, pageSize: 500 })
        this.deptOptions = res.data || []
      } catch (e) { this.deptOptions = [] }
    },
    // 导出用户统计 Excel：条件与列表筛选保持一致；
    // 后端导出暂不支持按用户ID筛选，填了用户ID时提示避免误解
    async handleExport () {
      if (this.query.zauId) {
        this.$message.info('导出暂不支持按用户ID筛选，将按部门/用户名条件导出')
      }
      this.exportLoading = true
      try {
        const exportParams = {
          zadId: this.query.zadId || undefined,
          zauName: this.query.zauName || undefined
        }
        if (this.query.dateRange && this.query.dateRange.length === 2) {
          exportParams.startDate = this.query.dateRange[0]
          exportParams.endDate = this.query.dateRange[1]
        }
        await exportPkqUserStatisticsList(exportParams)
        this.$message.success('导出成功')
      } catch (e) { /* 失败提示已在 exportFile 内统一处理 */ } finally { this.exportLoading = false }
    },
    async handleDetail (row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        const res = await getPkqUserStatisticsByZauId({ pusId: row.pusId })
        this.detail = res.data || null
      } catch (e) { this.detail = null } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.stat-detail { min-height: 120px; }
.stat-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 16px; border-bottom: 1px dashed #e5e7eb;
  font-size: 14px;
  span { color: #64748b; }
  b { color: #1e293b; font-size: 15px; }
}
</style>
